import type { TopicItem, TopicAvailability } from '@features/CardsWorkspace/api/topicCardsApi';
import type { LookupSense } from '@features/CardsWorkspace/api/workspaceApi';
import { canonicalPos } from '../../features/CardsWorkspace/lib/pos';

export interface TopicPair {
  id: string;
  word: string;
  pos: string | null;
  translation: string;
  /** Значение из словарного снимка подборки: помета, синонимы, пример. Нет в снимке — null. */
  details: LookupSense | null;
}

export interface TopicGroup {
  word: string;
  /** Транскрипция из снимка подборки. */
  ipa: string | null;
  /** Несколько слов: фраза или фразовый глагол (все значения — глаголы). */
  kind: 'phrase' | 'phrasal' | null;
  pairs: TopicPair[];
}

export type PairAvailability =
  | { state: 'available'; cost: 0 | 1 }
  | { state: 'learned' | 'generating' | 'unavailable' | 'error'; cost: 0 };

const normalize = (value: string) => value.normalize('NFKC').trim().toLowerCase().replace(/\s+/g, ' ');

export function topicGroups(items: TopicItem[]): TopicGroup[] {
  const groups = new Map<string, TopicGroup>();
  const seen = new Set<string>();
  for (const item of items) {
    const wordKey = normalize(item.word);
    if (!groups.has(wordKey)) {
      groups.set(wordKey, { word: item.word, ipa: item.lookup.transcription || null, kind: null, pairs: [] });
    }
    const dictionary = [...item.lookup.active, ...item.lookup.other];
    for (const sense of item.recommendedSenses?.length ? item.recommendedSenses : [item.recommended]) {
      const id = JSON.stringify([wordKey, canonicalPos(sense.pos), normalize(sense.translation)]);
      if (seen.has(id)) continue;
      seen.add(id);
      const details =
        dictionary.find(
          (entry) =>
            canonicalPos(entry.pos) === canonicalPos(sense.pos) && normalize(entry.main) === normalize(sense.translation),
        ) ?? null;
      groups.get(wordKey)!.pairs.push({ id, word: item.word, ...sense, details });
    }
  }
  return [...groups.values()].map((group) => ({
    ...group,
    kind: !group.word.trim().includes(' ')
      ? null
      : group.pairs.every((pair) => canonicalPos(pair.pos) === 'verb')
        ? 'phrasal'
        : 'phrase',
  }));
}

/** Только сохранённая тематическая связка; другой перевод никогда не подставляем. */
export function pairAvailability(pair: TopicPair, availability: TopicAvailability): PairAvailability {
  const senses = availability.items
    .filter((candidate) => normalize(candidate.word) === normalize(pair.word))
    .flatMap((item) => item.senses);
  const sense = senses.find(
    (candidate) =>
      canonicalPos(candidate.pos) === canonicalPos(pair.pos) &&
      normalize(candidate.translation) === normalize(pair.translation),
  );
  if (!sense?.available) return { state: 'unavailable', cost: 0 };
  if (sense.learned) return { state: 'learned', cost: 0 };
  if (sense.generating) return { state: 'generating', cost: 0 };
  return { state: 'available', cost: sense.ready === true ? 0 : 1 };
}

export function groupCost(group: TopicGroup, states: Record<string, PairAvailability>): number {
  return group.pairs.some((pair) => states[pair.id]?.state === 'available' && states[pair.id]?.cost === 1) ? 1 : 0;
}

/** Значений за одно нажатие — как у одного слова (MAX_PICKS_PER_CREATE на бэке). */
export const PACK_PICKS = 10;

/** Пачки для нажатий: до 10 значений от разных слов = одна AI-генерация. Слово целиком
 * в одной пачке; готовые слова пакуются отдельно, чтобы не занимать место в платных. */
export function topicPacks(groups: TopicGroup[], states: Record<string, PairAvailability>): TopicGroup[][] {
  const packs: { groups: TopicGroup[]; size: number; paid: boolean }[] = [];
  for (const group of groups) {
    const paid = groupCost(group, states) === 1;
    const pack = packs.find((candidate) => candidate.paid === paid && candidate.size + group.pairs.length <= PACK_PICKS);
    if (pack) {
      pack.groups.push(group);
      pack.size += group.pairs.length;
    } else packs.push({ groups: [group], size: group.pairs.length, paid });
  }
  return packs.map((pack) => pack.groups);
}

export function packCost(pack: TopicGroup[], states: Record<string, PairAvailability>): number {
  return pack.some((group) => groupCost(group, states) === 1) ? 1 : 0;
}

export function selectionCost(groups: TopicGroup[], states: Record<string, PairAvailability>): number {
  return topicPacks(groups, states).reduce((total, pack) => total + packCost(pack, states), 0);
}

export interface BulkProgress {
  phase: 'checking' | 'creating';
  processed: number;
  total: number;
  word?: string;
}

export interface BulkResult {
  added: string[];
  already: string[];
  failed: Record<string, string>;
  quota?: { required: number; available: number };
  interrupted: boolean;
}

/** Одно нажатие «Создать N карточек» — одна запись «Генераций»: все пачки шлют один
 *  `generation_id`, `generation_part` — номер пачки (ТЗ главной § 22.12). */
export interface PackPress {
  generationId: string;
  part: number;
}

interface BulkDependencies {
  isActive: () => boolean;
  availability: () => Promise<TopicAvailability>;
  /** Дневные жетоны и несгораемые (с 2026-10-22): вместе — сколько пачек можно оплатить. */
  quota: () => Promise<{ remaining: number; bonus: number }>;
  /** Один запрос на пачку; `skipped` — слова, которые сервер пропустил. */
  create: (pack: TopicGroup[], press: PackPress) => Promise<{ skipped: string[] }>;
  errorCode: (error: unknown) => string;
  onProgress: (progress: BulkProgress) => void;
  onAccepted: (ids: string[]) => void;
}

/**
 * После каких ошибок следующие пачки не отправляем. Исход неизвестен (NETWORK, SERVER,
 * GENERATION_PART_IN_PROGRESS) — перепроверяем доступностью, без повторного POST; RATE_LIMIT,
 * MODULE_NOT_FOUND, EMAIL_NOT_VERIFIED и прочее неизвестное — следующие тоже не пройдут.
 * «Уже есть», «ещё генерируется», «нет в словаре» касаются только своей пачки.
 */
const STOP_CODES = new Set([
  'DAILY_LIMIT_REACHED',
  'NETWORK',
  'SERVER',
  'GENERATION_PART_IN_PROGRESS',
  'RATE_LIMIT',
  'MODULE_NOT_FOUND',
  'EMAIL_NOT_VERIFIED',
  'UNKNOWN',
]);

/** Предварительно проверяем всю сумму, затем отправляем пачками до 10 значений.
 * Ошибки создания не ретраим автоматически: сначала выясняем, принят ли запрос.
 * `generationId` — id этого нажатия: модель не создаёт его сама (без alias-импортов). */
export async function createTopicSelection(
  groups: TopicGroup[],
  deps: BulkDependencies,
  generationId: string,
): Promise<BulkResult> {
  const result: BulkResult = { added: [], already: [], failed: {}, interrupted: false };
  const active = () => {
    if (!deps.isActive()) result.interrupted = true;
    return !result.interrupted;
  };
  const markFailed = (pairs: TopicPair[], code: string) =>
    pairs.forEach((pair) => {
      result.failed[pair.id] = code;
    });
  const acceptExisting = (pairs: TopicPair[], availability: TopicAvailability) => {
    const present = pairs.filter((pair) =>
      ['learned', 'generating'].includes(pairAvailability(pair, availability).state),
    );
    const ids = present.map((pair) => pair.id);
    result.already.push(...ids);
    deps.onAccepted(ids);
    return new Set(ids);
  };
  deps.onProgress({ phase: 'checking', processed: 0, total: groups.length });
  const states: Record<string, PairAvailability> = {};
  let availability;
  try {
    availability = await deps.availability();
  } catch (error) {
    if (active()) groups.forEach((group) => markFailed(group.pairs, deps.errorCode(error)));
    return result;
  }
  if (!active()) return result;
  const checked = groups.map((group) => {
    acceptExisting(group.pairs, availability);
    group.pairs.forEach((pair) => {
      states[pair.id] = pairAvailability(pair, availability);
    });
    const pairs = group.pairs.filter((pair) => states[pair.id].state === 'available');
    markFailed(
      group.pairs.filter((pair) => states[pair.id].state === 'unavailable'),
      'NOT_IN_DICTIONARY',
    );
    if (pairs.length > 10) {
      markFailed(pairs, 'PICK_LIMIT');
      return { ...group, pairs: [] };
    }
    return { ...group, pairs };
  });
  if (!active()) return result;
  const pending = checked.filter((group) => group.pairs.length);
  if (!pending.length) return result;
  let quota;
  try {
    quota = await deps.quota();
  } catch (error) {
    pending.forEach((group) => markFailed(group.pairs, deps.errorCode(error)));
    return result;
  }
  if (!active()) return result;
  const packs = topicPacks(pending, states);
  const required = packs.reduce((total, pack) => total + packCost(pack, states), 0);
  const available = quota.remaining + quota.bonus;
  if (required > available) {
    result.quota = { required, available };
    return result;
  }
  // После ошибки или пропуска — только чтение, никакого повторного POST.
  const settle = async (pairs: TopicPair[], code: string) => {
    let present = new Set<string>();
    let fresh: TopicAvailability | null = null;
    try {
      fresh = await deps.availability();
      if (active()) present = acceptExisting(pairs, fresh);
    } catch {
      /* Не подтверждённые связки остаются выбранными для явного повтора. */
    }
    if (!active()) return;
    pairs
      .filter((pair) => !present.has(pair.id))
      .forEach((pair) => {
        result.failed[pair.id] =
          fresh && pairAvailability(pair, fresh).state === 'unavailable' ? 'NOT_IN_DICTIONARY' : code;
      });
  };
  let remaining = available;
  for (let index = 0; index < packs.length; index++) {
    if (!active()) break;
    const pack = packs[index];
    const word = pack.map((group) => group.word).join(', ');
    deps.onProgress({ phase: 'creating', processed: index, total: packs.length, word });
    const attempted = pack.flatMap((group) => group.pairs);
    try {
      const cost = packCost(pack, states);
      if (cost > remaining) {
        result.quota = { required: cost, available: remaining };
        break;
      }
      const { skipped } = await deps.create(pack, { generationId, part: index });
      remaining -= cost;
      const skippedWords = new Set(skipped.map(normalize));
      const ids = pack
        .filter((group) => !skippedWords.has(normalize(group.word)))
        .flatMap((group) => group.pairs.map((pair) => pair.id));
      result.added.push(...ids);
      if (active()) deps.onAccepted(ids);
      const missed = pack.filter((group) => skippedWords.has(normalize(group.word))).flatMap((group) => group.pairs);
      if (missed.length && active()) await settle(missed, 'UNKNOWN');
    } catch (error) {
      if (!active()) break;
      const code = deps.errorCode(error);
      await settle(attempted, code);
      if (!active()) break;
      if (STOP_CODES.has(code)) break;
    } finally {
      if (active()) deps.onProgress({ phase: 'creating', processed: index + 1, total: packs.length });
    }
  }
  return result;
}
